import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";

/**
 * Master v5 Prompt 24 — TanStack Query hooks for Rehearsal, transcription
 * and deterministic speech-evidence metrics. Mirrors practiceQueries.js's
 * `request()` error contract exactly (`.status`/`.body` on thrown errors).
 */
async function request(path, options = {}) {
  const res = await rrFetch(path, options);
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && (body.detail?.error || body.detail?.message || body.detail)) || "request_failed");
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const jsonPost = (path, payload) => request(path, { method: "POST", body: JSON.stringify(payload ?? {}) });

export const rqk = {
  byInstance: (wrkId, iinId) => ["rehearsal-by-instance", wrkId, iinId],
  detail: (wrkId, rehId) => ["rehearsal-detail", wrkId, rehId],
  list: (wrkId, filters) => ["rehearsal-list", wrkId, filters],
  comparison: (wrkId, cmpId) => ["rehearsal-comparison", wrkId, cmpId],
};

export function useRehearsalByInstance(workspaceId, iinId) {
  return useQuery({
    queryKey: rqk.byInstance(workspaceId, iinId),
    enabled: !!workspaceId && !!iinId,
    retry: false,
    // Polls automatically while a TranscriptionJob is queued/processing,
    // stops on its own once the job reaches a terminal state.
    refetchInterval: (query) => {
      const job = query.state.data?.transcription_job;
      return job && !["succeeded", "failed"].includes(job.status) ? 4000 : false;
    },
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/rehearsals/by-instance/${iinId}`),
  });
}

export function useRehearsalDetail(workspaceId, rehId, { poll = false } = {}) {
  return useQuery({
    queryKey: rqk.detail(workspaceId, rehId),
    enabled: !!workspaceId && !!rehId,
    retry: false,
    // Polling is only turned on while a TranscriptionJob is queued/processing —
    // callers stop it once the job reaches a terminal state.
    refetchInterval: poll ? 4000 : false,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/rehearsals/${rehId}`),
  });
}

export function useRehearsalList(workspaceId, filters = {}) {
  const params = new URLSearchParams(filters).toString();
  return useQuery({
    queryKey: rqk.list(workspaceId, filters),
    enabled: !!workspaceId,
    retry: false,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/rehearsals${params ? `?${params}` : ""}`),
  });
}

export function useCreateOrGetRehearsal(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ iinId, contextType, sectionId, beatId, presenceContextId }) =>
      jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/rehearsals`, {
        iin_id: iinId,
        context_type: contextType,
        section_id: sectionId || null,
        beat_id: beatId || null,
        presence_context_id: presenceContextId || null,
      }),
    // The response is the full detail envelope ({rehearsal, transcription_job,
    // transcript, metrics}), not a bare Rehearsal — and every mutation here
    // invalidates by workspace-only PREFIX (TanStack's default partial-key
    // match) rather than needing the exact iin_id/reh_id in scope, so the
    // renderer's own useRehearsalByInstance subscription always refetches.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rehearsal-by-instance", workspaceId] }),
  });
}

export function useUploadRehearsalRecording(workspaceId, rehId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append("upload", file, file.name);
      return request(`/api/rehearsal-room/workspaces/${workspaceId}/rehearsals/${rehId}/recording`, {
        method: "POST",
        body: form,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rehearsal-by-instance", workspaceId] }),
  });
}

export function useRetryTranscription(workspaceId, rehId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/rehearsals/${rehId}/transcription/retry`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rehearsal-by-instance", workspaceId] }),
  });
}

export function useCreateComparison(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ baselineRehId, latestRehId }) =>
      jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/rehearsal-comparisons`, {
        baseline_reh_id: baselineRehId,
        latest_reh_id: latestRehId,
      }),
    onSuccess: (cmp) => qc.invalidateQueries({ queryKey: rqk.comparison(workspaceId, cmp.comparison?.cmp_id) }),
  });
}

export function useComparisonDetail(workspaceId, cmpId) {
  return useQuery({
    queryKey: rqk.comparison(workspaceId, cmpId),
    enabled: !!workspaceId && !!cmpId,
    retry: false,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/rehearsal-comparisons/${cmpId}`),
  });
}
